import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import Lenis from "lenis";
import { HelmetProvider } from "react-helmet-async";

import AgeGate from "@/components/AgeGate";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import Ribbon from "@/components/Ribbon";
import Story from "@/components/Story";
import Products from "@/components/Products";
import WhyMatters from "@/components/WhyMatters";
import Cocktails from "@/components/Cocktails";
import { Press } from "@/components/FindUsPress";
import SocialWall from "@/components/SocialWall";
import Faq from "@/components/Faq";
import Inquire from "@/components/Inquire";
import Footer from "@/components/Footer";
import PressKit from "@/pages/PressKit";
import Journal from "@/pages/Journal";
import Article from "@/pages/Article";
import NotFound from "@/pages/NotFound";
import CocktailsGallery from "@/pages/CocktailsGallery";
import CocktailRecipe from "@/pages/CocktailRecipe";
import { HOME_JSONLD } from "@/data/homeJsonld";
import { Helmet } from "react-helmet-async";

const HomePage = () => {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!entered) return;
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, [entered]);

  return (
    <div className="App relative">
      <Helmet>
        <title>POG Spirits — Hawaiian Vodka & RTD Punch</title>
        <meta name="description" content="POG Spirits — an independent Hawaiian spirits brand created in partnership with Meadow Gold and inspired by the 1971 POG (Passion Orange Guava) juice. POG Vodka 750ml (35% ABV) and POG Spiked Island Punch RTD (6% ABV). Distributed across Hawai‘i by Reyes / Island Distributing. Icons evolve. So we did. 21+." />
        <link rel="canonical" href="https://pogspirits.com/" />
        <meta property="og:url" content="https://pogspirits.com/" />
        <meta property="og:title" content="POG Spirits — Hawaiian Vodka & RTD Punch" />
        <meta property="og:description" content="POG Vodka 750ml and POG Spiked Island Punch RTD. An independent spirits brand created in partnership with Meadow Gold, inspired by the 1971 POG juice. Distributed by Reyes / Island Distributing. 21+." />
        <meta property="og:image" content="https://pogspirits.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="POG Spirits — Icons evolve. So we did." />
        <meta name="twitter:description" content="POG Vodka 750ml & POG Spiked Island Punch RTD. Hawai‘i's icon, now in a bottle. 21+." />
        <meta name="twitter:image" content="https://pogspirits.com/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify(HOME_JSONLD)}</script>
      </Helmet>
      <AgeGate onVerified={() => setEntered(true)} />
      <Nav />
      <main>
        <Hero />
        <Ribbon />
        <Story />
        <Products />
        <WhyMatters />
        <Cocktails />
        <Press />
        <SocialWall />
        <Faq />
        <Inquire />
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/press-kit" element={<PressKit />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/journal/:slug" element={<Article />} />
          <Route path="/cocktails" element={<CocktailsGallery />} />
          <Route path="/cocktails/:slug" element={<CocktailRecipe />} />
          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#14100D",
              color: "#EFE7D6",
              border: "1px solid rgba(239,231,214,0.15)",
              fontFamily: "Outfit, sans-serif",
              borderRadius: 0,
              letterSpacing: "0.02em",
            },
          }}
        />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
